import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slide,
  useTheme,
  Theme,
  Tooltip,
  IconButton,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { useState } from "react";

import { SafeArgonButton as Button } from "0.Mine/4.Argon/ArgonComponents";

// Mapping display options to AI-friendly values
const goalOptions: { label: string; value: string }[] = [
  { label: "Voglio avere piu vendite", value: "get more sales" },
  { label: "Voglio fidelizzare i miei clienti", value: "improve customer loyalty" },
  { label: "Voglio fare upselling sui miei clienti", value: "upsell to existing clients" },
  { label: "Voglio trasformare i lead in prospect", value: "convert leads to prospect" },
  {
    label: "Voglio trasformare i prospect in clienti",
    value: "convert interested people to clients",
  },
  { label: "Voglio aumentare l awareness", value: "increase product awareness" },
];

const toneOptions: { label: string; value: string }[] = [
  { label: "consulente professionale", value: "professional business consultant" },
  { label: "Marketing Coach amichevole", value: "friendly marketing coach" },
  { label: "Copywriter Creativo", value: "creative copywriter" },
  { label: "Specialista tecnico", value: "technical specialist" },
  { label: "Speaker Motivazionale", value: "motivational speaker" },
];

// Transition for animation
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface WorkflowSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (goal: string, tone: string) => void;
}

export default function WorkflowSettingsModal({
  open,
  onClose,
  onSubmit,
}: WorkflowSettingsModalProps) {
  const theme = useTheme<Theme>();
  const [selectedGoal, setSelectedGoal] = useState<string>("");
  const [selectedTone, setSelectedTone] = useState<string>("");

  const handleSubmit = () => {
    if (selectedGoal && selectedTone) {
      onSubmit(selectedGoal, selectedTone);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Seleziona il tuo obiettivo e assistente</DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth margin="normal">
          <InputLabel
            id="goal-label"
            sx={{
              transform: "translate(11px, 8px) scale(1)",
              transition: "all 0.2s ease",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          >
            Obiettivo
          </InputLabel>
          <Select
            labelId="goal-label"
            value={selectedGoal}
            label="Business Goal"
            onChange={(e) => setSelectedGoal(e.target.value)}
            endAdornment={
              <Tooltip
                title="Seleziona l'obiettivo principale che desideri raggiungere con questa sequenza di automazione. L'AI ottimizzerà i messaggi in base a questo obiettivo."
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    right: 32, // Position before the dropdown arrow
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            {goalOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel
            id="tone-label"
            sx={{
              transform: "translate(11px, 8px) scale(1)",
              transition: "all 0.2s ease",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          >
            Assistente
          </InputLabel>
          <Select
            labelId="tone-label"
            value={selectedTone}
            label="Desired Tone"
            onChange={(e) => setSelectedTone(e.target.value)}
            endAdornment={
              <Tooltip
                title="In che stile vuoi che l'AI scriva i messaggi? Scegli un assistente che meglio si adatta al tuo obiettivo."
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    right: 32, // Position before the dropdown arrow
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            {toneOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Annulla
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedGoal || !selectedTone}
        >
          Genera automazione con AI
        </Button>
      </DialogActions>
    </Dialog>
  );
}
